import { useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Plus } from "lucide-react";
import {
  SkillFormDialog,
  SkillFormDialogMode,
  type SkillFormValues,
  type SkillSubmitResult,
} from "~/components/assistant/SkillFormDialog";
import SkillPickerModal from "~/components/assistant/SkillPickerModal";
import { SkillRow } from "~/components/assistant/SkillRow";
import type { LibrarySkill } from "~/components/assistant/hooks/useSkillLibrary";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { useDatasetAttachedSkills } from "~/pages/datasets/components/assistant/hooks/useDatasetAttachedSkills";
import { ConfirmDestructiveDialog } from "~/pages/manifold-nextgen/components/ConfirmDestructiveDialog";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import {
  SKILL_NAME_MAX_LENGTH,
  getSkill,
  invalidateAllSkillLists,
  updateSkill,
  type PartialSkill,
  type Skill,
} from "~/services/skills";
import {
  extractErrorCode,
  getApiErrorMessage,
  isApiError,
  parseApiErrorBody,
} from "~/utils/apiClient";
import rumClient from "~/utils/monitoring";

type DialogContent =
  | { kind: "none" }
  | { kind: "picker" }
  | { kind: "create"; initialData?: Partial<SkillFormValues>; sourceName?: string }
  | { kind: "edit"; skill: Skill };

export const DatasetAISkillsTab = () => {
  const { data: dataset } = useDataset();
  const { showError, showSuccess } = useToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const { skills, isLoading, createSkill, detach } = useDatasetAttachedSkills(dataset?.id);

  const [dialog, setDialog] = useState<DialogContent>({ kind: "none" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PartialSkill | null>(null);

  const closeDialog = () => setDialogOpen(false);

  const handlePickFromLibrary = async (skill: LibrarySkill) => {
    try {
      const sourceSkill = await getSkill(skill.id);
      setDialog({
        kind: "create",
        sourceName: sourceSkill.name,
        initialData: {
          name: `${sourceSkill.name}-copy`.slice(0, SKILL_NAME_MAX_LENGTH),
          description: sourceSkill.description,
          content: sourceSkill.content,
        },
      });
    } catch {
      showError("Unable to load skill from the library");
    }
  };

  const handleCreateNew = () => setDialog({ kind: "create" });

  const handleEdit = async (skillId: string) => {
    try {
      const skill = await getSkill(skillId);
      setDialog({ kind: "edit", skill });
      setDialogOpen(true);
    } catch {
      showError("Unable to load skill for editing");
    }
  };

  const handleEditorSubmit = async (data: SkillFormValues): Promise<SkillSubmitResult> => {
    if ((dialog.kind !== "edit" && dialog.kind !== "create") || !dataset?.id) {
      return { ok: false };
    }
    try {
      if (dialog.kind === "edit") {
        await updateSkill(dialog.skill.id, data);
        await invalidateAllSkillLists();
        showSuccess(`"${data.name}" updated`);
      } else {
        await createSkill(data);
        showSuccess(`"${data.name}" added`);
      }
      closeDialog();
      return { ok: true };
    } catch (e) {
      rumClient?.recordError(e);
      if (isApiError(e) && e.status === 409) {
        const code = extractErrorCode(parseApiErrorBody(e));
        if (code === "conflict" || code === null) {
          const message = `A skill with this name already exists on "${dataset.title}".`;
          showError(`${message} Choose a different name.`);
          return { ok: false, field: "name", message };
        }
      }
      showError(isApiError(e) ? getApiErrorMessage(e) : "Unable to save skill");
      return { ok: false };
    }
  };

  const handleDeleteConfirm = async (skill: PartialSkill) => {
    try {
      await detach(skill.id);
      showSuccess(`"${skill.name}" removed`);
    } catch (e) {
      rumClient?.recordError(e);
      showError("Unable to remove skill from dataset");
      throw e;
    }
  };

  const skillFormInitialData =
    dialog.kind === "edit"
      ? {
          name: dialog.skill.name,
          description: dialog.skill.description ?? "",
          content: dialog.skill.content,
        }
      : dialog.kind === "create"
        ? dialog.initialData
        : undefined;

  return (
    <>
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <TabHeader
          title="AI Skills"
          showReadOnlyBadge={!isOwner}
          context={isLoading ? undefined : `${skills.length} installed`}
          description="Skills define dataset-specific instructions and workflows. When this dataset is attached to a project, its skills become available to the project's AI agent."
          action={
            isOwner && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDialog({ kind: "picker" });
                  setDialogOpen(true);
                }}
                className="text-primary hover:text-primary/80"
              >
                <Plus />
                Add skill
              </Button>
            )
          }
        />

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : skills.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">No skills installed.</p>
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {skills.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                canEdit={isOwner}
                onEdit={() => handleEdit(skill.id)}
                onDelete={() => setDeleteTarget(skill)}
              />
            ))}
          </ul>
        )}
      </div>

      <SkillPickerModal
        open={dialogOpen && dialog.kind === "picker"}
        onClose={closeDialog}
        onPick={handlePickFromLibrary}
        onCreateNew={handleCreateNew}
      />

      <SkillFormDialog
        open={dialogOpen && (dialog.kind === "create" || dialog.kind === "edit")}
        onClose={closeDialog}
        onSubmit={handleEditorSubmit}
        mode={dialog.kind === "edit" ? SkillFormDialogMode.EDIT : SkillFormDialogMode.CREATE}
        initialData={skillFormInitialData}
        duplicateSourceName={dialog.kind === "create" ? dialog.sourceName : undefined}
        description={
          dialog.kind === "create" && dialog.sourceName
            ? `Edit before saving — original is unchanged. This copy will be attached to "${dataset?.title}".`
            : `This skill will be attached to "${dataset?.title}".`
        }
      />

      <ConfirmDestructiveDialog
        target={deleteTarget}
        title="Remove this skill?"
        description={(skill) => (
          <>
            &ldquo;{skill.name}&rdquo; will be removed from this dataset. The skill remains in your
            library and can be re-attached at any time.
          </>
        )}
        confirmLabel="Remove"
        pendingLabel="Removing…"
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
};
